import { Check, Clipboard, Download, FileImage, ImagePlus, RotateCcw, UploadCloud, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ToolDefinition } from '../data/tools';
import { cn, formatBytes } from '../lib/utils';

type LoadedImage = { width: number; height: number; url: string };
type ResultFile = { url: string; blob: Blob; width: number; height: number; name: string };
type SocialPreset = { label: string; width: number; height: number };

const SOCIAL_PRESETS: Record<string, SocialPreset> = {
  instagramSquare: { label: 'Instagram square', width: 1080, height: 1080 },
  instagramPortrait: { label: 'Instagram portrait', width: 1080, height: 1350 },
  story: { label: 'Story / Reels', width: 1080, height: 1920 },
  youtube: { label: 'YouTube thumbnail', width: 1280, height: 720 },
  facebook: { label: 'Facebook cover', width: 820, height: 312 },
  linkedin: { label: 'LinkedIn post', width: 1200, height: 627 },
  xLandscape: { label: 'X landscape', width: 1600, height: 900 }
};

const RATIO_MAP: Record<string, number | null> = {
  original: null,
  '1:1': 1,
  '4:5': 4 / 5,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '3:2': 3 / 2,
  '2:3': 2 / 3
};

function extensionForMime(mime: string) {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}

function outputMime(format: string) {
  if (format === 'PNG') return 'image/png';
  if (format === 'WebP') return 'image/webp';
  return 'image/jpeg';
}

function safeBaseName(name: string) {
  return name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'image';
}

async function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality = 0.9) {
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Your browser could not create the output image.')), mime, quality);
  });
}

function drawContain(ctx: CanvasRenderingContext2D, image: HTMLImageElement, width: number, height: number) {
  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const w = image.naturalWidth * scale;
  const h = image.naturalHeight * scale;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
}

function coverRect(sourceW: number, sourceH: number, targetRatio: number, position: string) {
  const sourceRatio = sourceW / sourceH;
  let width = sourceW;
  let height = sourceH;
  if (sourceRatio > targetRatio) width = sourceH * targetRatio;
  else height = sourceW / targetRatio;
  let x = (sourceW - width) / 2;
  let y = (sourceH - height) / 2;
  if (position === 'left') x = 0;
  if (position === 'right') x = sourceW - width;
  if (position === 'top') y = 0;
  if (position === 'bottom') y = sourceH - height;
  return { x, y, width, height };
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

function dominantPalette(image: HTMLImageElement, count = 6) {
  const canvas = document.createElement('canvas');
  canvas.width = 72;
  canvas.height = 72;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return [] as string[];
  ctx.drawImage(image, 0, 0, 72, 72);
  const data = ctx.getImageData(0, 0, 72, 72).data;
  const groups = new Map<string, { n: number; r: number; g: number; b: number }>();
  for (let i = 0; i < data.length; i += 16) {
    const a = data[i + 3];
    if (a < 180) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const key = `${Math.round(r / 32)}-${Math.round(g / 32)}-${Math.round(b / 32)}`;
    const group = groups.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    group.n += 1;
    group.r += r;
    group.g += g;
    group.b += b;
    groups.set(key, group);
  }
  return [...groups.values()]
    .sort((a, b) => b.n - a.n)
    .slice(0, count)
    .map((group) => rgbToHex(Math.round(group.r / group.n), Math.round(group.g / group.n), Math.round(group.b / group.n)));
}

export function FreeImageWorkspace({ tool, compact = false }: { tool: ToolDefinition; compact?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [image, setImage] = useState<LoadedImage | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ResultFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [quality, setQuality] = useState(82);
  const [format, setFormat] = useState(tool.slug === 'convert' ? 'WebP' : 'JPG');
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [lockAspect, setLockAspect] = useState(true);
  const [ratio, setRatio] = useState('1:1');
  const [cropPosition, setCropPosition] = useState('center');
  const [socialPreset, setSocialPreset] = useState('instagramSquare');
  const [socialMode, setSocialMode] = useState('cover');
  const [sampledColor, setSampledColor] = useState('#000000');
  const [palette, setPalette] = useState<string[]>([]);
  const ToolIcon = tool.icon;

  const sourceMime = useMemo(() => {
    if (file?.type === 'image/png' || file?.type === 'image/webp' || file?.type === 'image/jpeg') return file.type;
    return 'image/jpeg';
  }, [file]);

  useEffect(() => () => {
    if (image?.url) URL.revokeObjectURL(image.url);
    if (result?.url) URL.revokeObjectURL(result.url);
  }, [image?.url, result?.url]);

  async function acceptFile(nextFile: File) {
    setError('');
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(nextFile.type)) {
      setError('Choose a JPG, PNG or WebP image for this browser tool.');
      return;
    }
    if (nextFile.size > 50 * 1024 * 1024) {
      setError('The image is larger than the 50 MB browser limit.');
      return;
    }
    if (image?.url) URL.revokeObjectURL(image.url);
    if (result?.url) URL.revokeObjectURL(result.url);
    const url = URL.createObjectURL(nextFile);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setFile(nextFile);
      setImage({ width: img.naturalWidth, height: img.naturalHeight, url });
      setWidth(img.naturalWidth);
      setHeight(img.naturalHeight);
      setResult(null);
      setPalette(tool.slug === 'palette-generator' ? dominantPalette(img) : []);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setError('This image could not be decoded by your browser.');
    };
    img.src = url;
  }

  function reset() {
    if (image?.url) URL.revokeObjectURL(image.url);
    if (result?.url) URL.revokeObjectURL(result.url);
    imageRef.current = null;
    setFile(null);
    setImage(null);
    setResult(null);
    setPalette([]);
    setSampledColor('#000000');
    setError('');
  }

  async function saveResult(canvas: HTMLCanvasElement, mime: string, outputQuality: number, suffix: string) {
    const blob = await canvasToBlob(canvas, mime, outputQuality);
    if (result?.url) URL.revokeObjectURL(result.url);
    const url = URL.createObjectURL(blob);
    setResult({
      url,
      blob,
      width: canvas.width,
      height: canvas.height,
      name: `${safeBaseName(file?.name ?? 'image')}-${suffix}.${extensionForMime(mime)}`
    });
  }

  async function processUtility() {
    const source = imageRef.current;
    if (!source || !file || !image) return;
    setBusy(true);
    setError('');
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas processing is not available in this browser.');

      if (tool.slug === 'compress') {
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.drawImage(source, 0, 0);
        const mime = format === 'PNG' ? 'image/png' : outputMime(format === 'JPG' ? 'JPG' : 'WebP');
        await saveResult(canvas, mime, quality / 100, 'compressed');
      } else if (tool.slug === 'resize') {
        const targetW = Math.max(1, Math.min(12000, Math.round(width)));
        const targetH = Math.max(1, Math.min(12000, Math.round(height)));
        if (targetW * targetH > 100_000_000) throw new Error('Keep the resized image below 100 megapixels.');
        canvas.width = targetW;
        canvas.height = targetH;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(source, 0, 0, targetW, targetH);
        await saveResult(canvas, sourceMime, 0.92, 'resized');
      } else if (tool.slug === 'crop') {
        const targetRatio = RATIO_MAP[ratio];
        const rect = targetRatio ? coverRect(image.width, image.height, targetRatio, cropPosition) : { x: 0, y: 0, width: image.width, height: image.height };
        canvas.width = Math.max(1, Math.round(rect.width));
        canvas.height = Math.max(1, Math.round(rect.height));
        ctx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, canvas.width, canvas.height);
        await saveResult(canvas, sourceMime, 0.94, 'cropped');
      } else if (tool.slug === 'convert') {
        canvas.width = image.width;
        canvas.height = image.height;
        if (format === 'JPG') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(source, 0, 0);
        await saveResult(canvas, outputMime(format), quality / 100, `converted-${format.toLowerCase()}`);
      } else if (tool.slug === 'social-resize') {
        const preset = SOCIAL_PRESETS[socialPreset];
        canvas.width = preset.width;
        canvas.height = preset.height;
        if (socialMode === 'contain') drawContain(ctx, source, preset.width, preset.height);
        else {
          const rect = coverRect(image.width, image.height, preset.width / preset.height, 'center');
          ctx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, preset.width, preset.height);
        }
        await saveResult(canvas, 'image/jpeg', 0.9, `${socialPreset}`);
      } else if (tool.slug === 'metadata-remover') {
        canvas.width = image.width;
        canvas.height = image.height;
        if (sourceMime === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(source, 0, 0);
        await saveResult(canvas, sourceMime, 0.96, 'metadata-clean');
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The browser could not process this image.');
    } finally {
      setBusy(false);
    }
  }

  function setResizeWidth(next: number) {
    const safe = Math.max(1, Math.min(12000, next || 1));
    setWidth(safe);
    if (lockAspect && image) setHeight(Math.max(1, Math.round(safe * image.height / image.width)));
  }

  function setResizeHeight(next: number) {
    const safe = Math.max(1, Math.min(12000, next || 1));
    setHeight(safe);
    if (lockAspect && image) setWidth(Math.max(1, Math.round(safe * image.width / image.height)));
  }

  async function copyColor(value: string) {
    try { await navigator.clipboard.writeText(value); } catch { /* Clipboard can be blocked by browser permissions. */ }
  }

  function pickColor(event: React.MouseEvent<HTMLImageElement>) {
    const source = imageRef.current;
    if (!source) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const scale = Math.min(rect.width / source.naturalWidth, rect.height / source.naturalHeight);
    const renderedWidth = source.naturalWidth * scale;
    const renderedHeight = source.naturalHeight * scale;
    const offsetX = (rect.width - renderedWidth) / 2;
    const offsetY = (rect.height - renderedHeight) / 2;
    const localX = event.clientX - rect.left - offsetX;
    const localY = event.clientY - rect.top - offsetY;
    if (localX < 0 || localY < 0 || localX > renderedWidth || localY > renderedHeight) return;
    const x = Math.min(source.naturalWidth - 1, Math.max(0, Math.floor(localX / renderedWidth * source.naturalWidth)));
    const y = Math.min(source.naturalHeight - 1, Math.max(0, Math.floor(localY / renderedHeight * source.naturalHeight)));
    const canvas = document.createElement('canvas');
    canvas.width = source.naturalWidth;
    canvas.height = source.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(source, 0, 0);
    const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
    setSampledColor(rgbToHex(r, g, b));
  }

  const isAnalysisTool = tool.slug === 'color-picker' || tool.slug === 'palette-generator';

  return (
    <section className={cn('workspace', 'free-workspace', compact && 'workspace-compact')}>
      <div className="workspace-toolbar">
        <div className="workspace-title">
          <span className="tool-icon"><ToolIcon size={20} /></span>
          <div><strong>{tool.name}</strong><span>Free · no sign-in · processed in your browser</span></div>
        </div>
        {file && <button className="text-button" type="button" onClick={reset}><RotateCcw size={16} /> Start over</button>}
      </div>

      {!file && (
        <div className={cn('dropzone', dragging && 'dragging')}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => { event.preventDefault(); setDragging(false); const dropped = event.dataTransfer.files[0]; if (dropped) void acceptFile(dropped); }}>
          <div className="dropzone-grid" aria-hidden="true" />
          <div className="dropzone-orbit" aria-hidden="true"><span /><span /><span /></div>
          <div className="dropzone-scan" aria-hidden="true" />
          <div className="upload-icon"><UploadCloud size={27} /><i aria-hidden="true" /></div>
          <p className="eyebrow">Free browser utility</p>
          <h2>Drop an image to {tool.shortName.toLowerCase()}</h2>
          <p>JPG, PNG or WebP · up to 50 MB · no AI credits</p>
          <button className="button button-primary" type="button" onClick={() => inputRef.current?.click()}><ImagePlus size={18} /> Choose image</button>
          <div className="privacy-line">For this free utility, processing stays in your browser.</div>
          <input ref={inputRef} className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => event.target.files?.[0] && void acceptFile(event.target.files[0])} />
        </div>
      )}

      {file && image && (
        <div className="workspace-ready">
          <div className="preview-panel free-preview-panel">
            <img src={result?.url ?? image.url} alt={result ? 'Processed browser result preview' : 'Selected upload preview'} onClick={tool.slug === 'color-picker' ? pickColor : undefined} className={tool.slug === 'color-picker' ? 'free-color-picker-image' : undefined} />
            <button className="preview-remove" type="button" onClick={reset} aria-label="Remove image"><X size={17} /></button>
            <div className="file-chip"><FileImage size={16} /><span>{file.name}</span><small>{formatBytes(file.size)}</small></div>
          </div>
          <div className="settings-panel">
            <div><p className="eyebrow">Free image utility</p><h3>{tool.name}</h3><p>{tool.description}</p></div>

            {tool.slug === 'compress' && <>
              <FreeSelect label="Output format" value={format} onChange={setFormat} values={['WebP', 'JPG', 'PNG']} />
              {format !== 'PNG' && <FreeRange label="Quality" value={quality} min={35} max={95} onChange={setQuality} suffix="%" />}
            </>}

            {tool.slug === 'resize' && <>
              <div className="free-control-grid"><FreeNumber label="Width" value={width} onChange={setResizeWidth} /><FreeNumber label="Height" value={height} onChange={setResizeHeight} /></div>
              <label className="free-check-row"><input type="checkbox" checked={lockAspect} onChange={(event) => setLockAspect(event.target.checked)} /> Keep original aspect ratio</label>
            </>}

            {tool.slug === 'crop' && <>
              <FreeSelect label="Aspect ratio" value={ratio} onChange={setRatio} values={Object.keys(RATIO_MAP)} />
              <FreeSelect label="Crop focus" value={cropPosition} onChange={setCropPosition} values={['center', 'top', 'bottom', 'left', 'right']} />
            </>}

            {tool.slug === 'convert' && <>
              <FreeSelect label="Convert to" value={format} onChange={setFormat} values={['WebP', 'JPG', 'PNG']} />
              {format !== 'PNG' && <FreeRange label="Quality" value={quality} min={50} max={100} onChange={setQuality} suffix="%" />}
            </>}

            {tool.slug === 'social-resize' && <>
              <FreeSelect label="Preset" value={socialPreset} onChange={setSocialPreset} values={Object.keys(SOCIAL_PRESETS)} display={(value) => `${SOCIAL_PRESETS[value].label} · ${SOCIAL_PRESETS[value].width}×${SOCIAL_PRESETS[value].height}`} />
              <FreeSelect label="Fit" value={socialMode} onChange={setSocialMode} values={['cover', 'contain']} display={(value) => value === 'cover' ? 'Fill & crop' : 'Fit with white space'} />
            </>}

            {tool.slug === 'color-picker' && <div className="free-color-result">
              <span className="free-color-swatch large" style={{ background: sampledColor }} />
              <div><small>Selected color</small><strong>{sampledColor}</strong></div>
              <button className="button button-ghost" type="button" onClick={() => void copyColor(sampledColor)}><Clipboard size={16} /> Copy HEX</button>
              <p>Click anywhere on the image preview to sample a pixel.</p>
            </div>}

            {tool.slug === 'palette-generator' && <div className="free-palette-grid">
              {palette.map((color) => <button type="button" key={color} onClick={() => void copyColor(color)} title={`Copy ${color}`}><span style={{ background: color }} /><strong>{color}</strong><small>Copy</small></button>)}
            </div>}

            {tool.slug === 'metadata-remover' && <div className="inline-notice">This creates a new image from the visible pixels, which removes common EXIF blocks. Keep your original if you need camera or capture metadata later.</div>}

            {error && <div className="inline-error">{error}</div>}

            {!isAnalysisTool && <button className="button button-primary button-large process-button" type="button" onClick={() => void processUtility()} disabled={busy}>
              <Check size={18} /> {busy ? 'Processing…' : result ? 'Process again' : 'Process in browser'}<span>Free</span>
            </button>}

            {result && <div className="free-result-summary">
              <div><small>Output</small><strong>{result.width} × {result.height}</strong></div>
              <div><small>File size</small><strong>{formatBytes(result.blob.size)}</strong></div>
              {tool.slug === 'compress' && <div><small>Change</small><strong>{result.blob.size < file.size ? `${Math.max(0, Math.round((1 - result.blob.size / file.size) * 100))}% smaller` : 'Larger than source'}</strong></div>}
              <a className="button button-primary" href={result.url} download={result.name}><Download size={17} /> Download result</a>
            </div>}

            <p className="settings-note">No upload to Nexhance AI is required for this utility. Browser support can affect available formats and maximum image size.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function FreeSelect({ label, value, onChange, values, display = (item: string) => item }: { label: string; value: string; onChange: (value: string) => void; values: string[]; display?: (value: string) => string }) {
  return <label className="option-control"><span className="option-label"><strong>{label}</strong></span><select className="free-native-select" value={value} onChange={(event) => onChange(event.target.value)}>{values.map((item) => <option key={item} value={item}>{display(item)}</option>)}</select></label>;
}

function FreeRange({ label, value, min, max, suffix, onChange }: { label: string; value: number; min: number; max: number; suffix: string; onChange: (value: number) => void }) {
  return <label className="option-control"><span className="option-label"><strong>{label}</strong></span><div className="range-wrap"><input type="range" min={min} max={max} value={value} onChange={(event) => onChange(Number(event.target.value))} /><span>{value}{suffix}</span></div></label>;
}

function FreeNumber({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return <label className="free-number-control"><span>{label}</span><input type="number" min="1" max="12000" value={value || ''} onChange={(event) => onChange(Number(event.target.value))} /></label>;
}
